import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators, NgForm } from '@angular/forms';
import { StateService } from '../state.service';
import { Subscription } from 'rxjs/Subscription';
import { FormService } from '../../../form.service';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import { catchError, takeUntil, map } from 'rxjs/operators';
import { SemsoSnackbarService } from '../../../semso-snackbar.service';
import { HttpError } from '../../../errors';
import * as moment from 'moment';

@Component({
  selector: 'app-internship-assignment',
  templateUrl: './internship-assignment.component.html',
  styleUrls: ['./internship-assignment.component.scss']
})
export class InternshipAssignmentComponent implements OnInit, OnDestroy {
  private assignmentForm: NgForm;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public attachment1 = false;
  public attachment2 = false;
  public attachmentFile1;
  public attachmentFile2;
  public attachmentText1;
  public attachmentText2;
  public assignmentData;
  public administrationData;
  public administrationDataCache;
  private formCode;
  private studentId;

  @ViewChild('assignmentForm') set content(content: NgForm) {
    this.assignmentForm = content;

    if (content !== undefined) {
      this.assignmentForm.statusChanges
        .takeUntil(this.destroy$)
        .subscribe(result => {
          this.stateService.updateAssignment(result === 'VALID');
        });
    }
  }

  constructor(private stateService: StateService,
    private formService: FormService, private route: ActivatedRoute, private snackbar: SemsoSnackbarService) { }

  ngOnInit() {
    this.route.params.takeUntil(this.destroy$).subscribe(params => {
      this.formCode = params['formCode'];
      this.studentId = params['studentId'];
      this.assignmentData = this.formService.getFormById(this.formCode)
        .takeUntil(this.destroy$)
        .pipe(catchError(error => {
          throw this.error(`Fout bij het ophalen van opdrachtinformatie voor formulier: ${this.formCode}`);
        }))
      this.administrationData = this.formService.getStudentAdministrationData(this.formCode)
        .takeUntil(this.destroy$)
        .pipe(
          catchError(e => {
            throw this.error(`Fout bij het ophalen van de administatieinformatie voor formulier: ${this.formCode}`);
          }),
          map(data => {
            const newData = Object.assign({}, data);
            newData.afname_Startdatum = moment(data.afname_Startdatum, 'DD-MM-YYYY', true).toDate();
            newData.afname_Einddatum = moment(data.afname_Einddatum, 'DD-MM-YYYY', true).toDate();
            return newData;
          })
        ).subscribe(data => {
          this.administrationData = data;
        });

      this.formService.getStudentAttachmentById(this.formCode, 1)
        .takeUntil(this.destroy$)
        .subscribe(data => {
          this.attachment1 = false;
          if (data != null) {
            this.attachment1 = data['base64Encoded'];
            if (data['base64Encoded']) {
              this.attachmentFile1 = data['waarde'];
            } else {
              this.attachmentText1 = data['waarde'];
            }
          }
        });

      this.formService.getStudentAttachmentById(this.formCode, 2)
        .takeUntil(this.destroy$)
        .subscribe(data => {
          this.attachment2 = false;
          if (data != null) {
            this.attachment2 = data['base64Encoded'];
            if (data['base64Encoded']) {
              this.attachmentFile2 = data['waarde'];
            } else {
              this.attachmentText2 = data['waarde'];
            }
          }
        });
    });
  };

  ngOnDestroy() {
    this.destroy$.next(true);
  };

  updateAdministrationData(data) {
    const newData = Object.assign({}, data);
    newData.afname_Startdatum = this.fixDateTime(newData.afname_Startdatum);
    newData.afname_Einddatum = this.fixDateTime(newData.afname_Einddatum);
    this.formService.postStudentAdmistrationData(this.formCode, newData)
      .takeUntil(this.destroy$)
      .subscribe(() => {
        this.stateService.updateAssignment(this.assignmentForm.valid);
      }, e => {
        throw this.error(`Fout bij het opslaan van de administatieinformatie voor formulier: ${this.formCode}`);
      });
  };

  supplementData(startsWith) {
    this.administrationDataCache = this.formService.getAdministrationDataCache().pipe(
      catchError(e => {
        throw this.error(`Fouit bij het ophalen van de cache administatieinformatie voor formulier: ${this.formCode}`);
      })
    ).subscribe(cache => {
      let properties = Object.getOwnPropertyNames(this.administrationData);
      properties.forEach(prop => {
        startsWith.forEach(needle => {
          if (prop.startsWith(needle)) {
            var value = this.administrationData[prop];
            if (value == null || value == '' || value == 'Invalid Date') {
              value = cache[prop];
              if (prop.endsWith("datum")) {
                value = moment(value, 'DD-MM-YYYY');
              }
            }
            this.administrationData[prop] = value;
          }
        })
      });
      this.updateAdministrationData(this.administrationData);
    });
  }

  updateAssignmentQuestion(data) {
    this.formService.postFormById(this.formCode, {
      opdrachtformuliervraagId: data.id,
      waarde: 'NietBeoordeeld',
      omschrijving: data.opmerkingen
    })
      .takeUntil(this.destroy$)
      .subscribe(() => {
        this.stateService.updateAssignment(this.assignmentForm.valid);
      }, e => {
        throw this.error(`Fout bij het opslaan van de opdrachtinformatie voor formulier: ${this.formCode}`);
      });
  };

  updateAttachmentText(text, number) {
    this.formService.postStudentAttachment(this.formCode, {
      volgnummer: number,
      waarde: text,
      base64encoded: false
    })
      .takeUntil(this.destroy$)
      .subscribe(() => {
        this.stateService.updateAssignment(this.assignmentForm.valid);
      }, e => {
        throw this.error(`Fout bij het opslaan van de bijlagetekst voor formulier: ${this.formCode}`);
      });
  };

  updateAttachmentFile(number) {
    let attachment;
    if (number === 1) {
      attachment = this.attachmentFile1;
    } else if (number === 2) {
      attachment = this.attachmentFile2;
    }
    if (attachment !== null) {
      this.formService.postStudentAttachment(this.formCode, {
        volgnummer: number,
        waarde: attachment,
        base64encoded: true
      })
        .takeUntil(this.destroy$)
        .subscribe(() => {
          this.stateService.updateAssignment(this.assignmentForm.valid);
        }, e => {
          this.error(`Fout bij het opslaan van de bijlage ${number} voor formulier: ${this.formCode}`);
        });
    } else {
      this.formService.deleteStudentAttachmentById(this.formCode, number)
        .takeUntil(this.destroy$)
        .subscribe(() => {
          this.stateService.updateAssignment(this.assignmentForm.valid);
        }, e => {
          throw this.error(`Fout bij het verwijderen van bijlage ${number} voor formulier: ${this.formCode}`);
        });
    }
  }

  private error(message) {
    this.snackbar.error(message);
    return new HttpError(message);
  }

  private fixDateTime(date) {
    if (!moment(date, 'YYYY-MM-DDT00:00:00', true).isValid()) {
      return date;
    }
    return moment(date).format('DD-MM-YYYY');
  }
}



// WEBPACK FOOTER //
// ./src/app/exam-preparation/assignment-pvb/internship-assignment/internship-assignment.component.ts